import { useCallback, useEffect, useMemo, useState } from 'react';
import { deletePreviewDraft, loadPreviewDraft, savePreviewDraft } from '../utils/previewStorage';
import { publishSnapshot } from '../api/travelPostPublishService';
import { SnapshotDay, SnapshotEntry, SnapshotNote, TravelPostPreviewDraft, TravelPostSnapshot } from '../types';
import { buildSnapshotFromItinerary } from '../utils/snapshotBuilder';

const cloneEntry = (entry: SnapshotEntry): SnapshotEntry => ({
  ...entry,
  notes: entry.notes.map((note) => ({ ...note, images: [...note.images] })),
  gallery: [...entry.gallery],
});

const cloneDay = (day: SnapshotDay): SnapshotDay => ({
  ...day,
  entries: day.entries.map(cloneEntry),
});

const recomputeMeta = (draft: TravelPostPreviewDraft): TravelPostPreviewDraft => {
  const visibleDays = draft.days.filter((day) => !day.hidden && day.entries.some((entry) => !entry.hidden));
  const entryCount = visibleDays.reduce((acc, day) => acc + day.entries.filter((entry) => !entry.hidden).length, 0);
  const mediaCount = visibleDays.reduce((acc, day) => acc + day.entries.reduce((entrySum, entry) => {
    if (entry.hidden) return entrySum;
    const galleryCount = entry.gallery.length;
    const noteImageCount = entry.notes.reduce((noteSum, note) => noteSum + note.images.length, 0);
    return entrySum + galleryCount + noteImageCount;
  }, 0), 0);

  return {
    ...draft,
    meta: {
      ...draft.meta,
      dayCount: visibleDays.length,
      entryCount,
      mediaCount,
    },
  };
};

const sanitizeSnapshot = (draft: TravelPostPreviewDraft): TravelPostSnapshot => {
  const visibleDays = draft.days
    .filter((day) => !day.hidden)
    .map((day) => ({
      ...day,
      entries: day.entries.filter((entry) => !entry.hidden),
    }))
    .filter((day) => day.entries.length > 0);

  return {
    schemaVersion: draft.schemaVersion,
    generatedAt: draft.generatedAt,
    title: draft.title,
    summary: draft.summary,
    city: draft.city,
    coverImage: draft.coverImage,
    tags: draft.tags,
    days: visibleDays,
    meta: {
      ...draft.meta,
      dayCount: visibleDays.length,
      entryCount: visibleDays.reduce((acc, day) => acc + day.entries.length, 0),
    },
  };
};

export const useTravelPostPreviewEditor = (draftId?: string) => {
  const [draft, setDraft] = useState<TravelPostPreviewDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!draftId) {
      setError('缺少預覽 ID');
      setLoading(false);
      return;
    }
    const stored = loadPreviewDraft(draftId);
    if (!stored) {
      setError('找不到對應的預覽資料，請重新從行程發佈');
      setLoading(false);
      return;
    }
    setDraft(stored);
    setLoading(false);
  }, [draftId]);

  const persist = useCallback((next: TravelPostPreviewDraft) => {
    const normalized = recomputeMeta({ ...next, days: next.days.map(cloneDay) });
    savePreviewDraft(normalized);
    setDraft(normalized);
  }, []);

  const setEntryHidden = useCallback((dayId: string, entryId: string, hidden: boolean) => {
    if (!draft) return;
    const nextDays = draft.days.map((day) => {
      if (day.id !== dayId) return day;
      return {
        ...day,
        entries: day.entries.map((entry) => (entry.id === entryId ? { ...entry, hidden } : entry)),
      };
    });
    persist({ ...draft, days: nextDays });
  }, [draft, persist]);

  const removeEntry = useCallback((dayId: string, entryId: string) => setEntryHidden(dayId, entryId, true), [setEntryHidden]);
  const restoreEntry = useCallback((dayId: string, entryId: string) => setEntryHidden(dayId, entryId, false), [setEntryHidden]);

  const restoreAll = useCallback(() => {
    if (!draft) return;
    const nextDays = draft.days.map((day) => ({
      ...day,
      hidden: false,
      entries: day.entries.map((entry) => ({ ...entry, hidden: false })),
    }));
    persist({ ...draft, days: nextDays });
  }, [draft, persist]);

  const addNoteToEntry = useCallback((dayId: string, entryId: string, text: string) => {
    if (!draft) return;
    const content = text.trim();
    if (!content) return;
    const newNote: SnapshotNote = {
      id: `note-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      text: content,
      images: [],
      createdAt: new Date().toISOString(),
    };

    const nextDays = draft.days.map((day) => {
      if (day.id !== dayId) return day;
      return {
        ...day,
        entries: day.entries.map((entry) => {
          if (entry.id === entryId || entry.sourceEntryId === entryId) {
            return {
              ...entry,
              notes: [...entry.notes, newNote],
            };
          }
          return entry;
        }),
      };
    });

    persist({ ...draft, days: nextDays });
  }, [draft, persist]);

  const updateTitle = useCallback((title: string) => {
    if (!draft) return;
    persist({ ...draft, title });
  }, [draft, persist]);

  const updateSummary = useCallback((summary: string) => {
    if (!draft) return;
    persist({ ...draft, summary });
  }, [draft, persist]);

  const updateTags = useCallback((tags: string[]) => {
    if (!draft) return;
    persist({ ...draft, tags });
  }, [draft, persist]);

  const updateSummaryAndTags = useCallback((summary: string, tags: string[]) => {
    if (!draft) return;
    persist({ ...draft, summary, tags });
  }, [draft, persist]);

  const updateCardGallery = useCallback((cardId: string, gallery: any[]) => {
    if (!draft) return;
    
    // 1. 更新 previewItinerary 中对应卡片的 image_gallery（如果存在）
    const updatedPreviewItinerary = draft.previewItinerary ? {
      ...draft.previewItinerary,
      days: draft.previewItinerary.days.map((day: any) => ({
        ...day,
        items: day.items?.map((item: any) => {
          const matchesCard = item.card?.id === cardId || item.card_id === cardId;
          if (matchesCard && item.card) {
            return {
              ...item,
              card: {
                ...item.card,
                image_gallery: gallery,
              },
            };
          }
          return item;
        }),
      })),
    } : draft.previewItinerary;
    
    // 2. 更新快照中的 gallery（draft.days 中的 entry.gallery）
    // 转换 gallery 数组为快照图片格式
    const snapshotImages = gallery.map((img: any) => ({
      url: img.url,
      alt: img.alt || '',
      key: img.key || img.id,
      uploaded_by: img.uploaded_by,
    }));
    
    const updatedSnapshotDays = draft.days.map((day) => ({
      ...day,
      entries: day.entries.map((entry) => {
        // 通过 card_id 匹配
        const matchesCard = entry.meta?.card_id === cardId;
        if (matchesCard) {
          console.log('[updateCardGallery] Updating snapshot gallery for entry:', entry.title, {
            oldGalleryLength: entry.gallery?.length,
            newGalleryLength: snapshotImages.length,
          });
          return {
            ...entry,
            gallery: snapshotImages,
            coverImage: snapshotImages[0] || entry.coverImage,
          };
        }
        return entry;
      }),
    }));
    
    const updatedDraft = {
      ...draft,
      days: updatedSnapshotDays,
      previewItinerary: updatedPreviewItinerary,
    };
    
    persist(updatedDraft);
    console.log('[updateCardGallery] Updated both previewItinerary and snapshot');
  }, [draft, persist]);

  const publish = useCallback(async () => {
    if (!draft || !draftId) throw new Error('缺少預覽資料');
    setPublishing(true);
    try {
      // 直接使用 draft（已经通过 updateCardGallery 更新了最新的图片数据）
      // 不需要重新生成快照，因为那样会丢失用户上传的图片
      console.log('[publish] Using existing draft with latest updates');
      
      // 保留原来的逻辑作为注释，以防未来需要
      // let freshSnapshot: TravelPostSnapshot;
      // if (draft.previewItinerary) {
      //   freshSnapshot = buildSnapshotFromItinerary({
      //     itinerary: draft.previewItinerary,
      //     title: draft.title,
      //     city: draft.city,
      //     coverImage: draft.coverImage,
      //     tags: draft.tags,
      //     sourceItineraryId: draft.sourceItineraryId,
      //   });
        // }
        
        // 旧的逻辑：应用用户编辑到重新生成的快照
        // 现在不需要了，因为 draft 已经包含所有最新的编辑和图片
        
      // 直接使用 draft，它已经包含：
      // 1. 用户上传的图片（通过 updateCardGallery 更新）
      // 2. 用户添加的 notes（通过 addNoteToEntry 更新）
      // 3. 隐藏的 entries（通过 removeEntry 更新）
      const snapshot = sanitizeSnapshot(draft);
      const post = await publishSnapshot({ snapshot });
      deletePreviewDraft(draftId);
      return post;
    } catch (err) {
      console.error('publish snapshot failed', err);
      setError(err instanceof Error ? err.message : '發佈失敗');
      throw err;
    } finally {
      setPublishing(false);
    }
  }, [draft, draftId]);

  const visibleDays = useMemo(() => {
    if (!draft) return [];
    return draft.days.filter((day) => !day.hidden);
  }, [draft]);

  const hiddenEntryIds = useMemo(() => {
    if (!draft) return new Set<string>();
    const ids = new Set<string>();
    draft.days.forEach((day) => {
      day.entries.forEach((entry) => {
        if (entry.hidden) {
          const sourceId = entry.sourceEntryId || entry.id;
          if (sourceId) ids.add(String(sourceId));
        }
      });
    });
    return ids;
  }, [draft]);

  const timelineDays = useMemo(() => draft?.previewItinerary?.days || [], [draft]);

  return {
    draft,
    visibleDays,
    timelineDays,
    hiddenEntryIds,
    loading,
    publishing,
    error,
    removeEntry,
    restoreEntry,
    restoreAll,
    addNoteToEntry,
    updateTitle,
    updateSummary,
    updateTags,
    updateSummaryAndTags,
    updateCardGallery,
    publish,
  };
};
